import * as React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    createSortedRowModel,
    rowSortingFeature,
    sortFn_alphanumeric,
    tableFeatures,
    useTable,
} from '@tanstack/react-table';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { getReviewsInfiniteQuery } from '../../api/reviewsApi';
import type { ListQueryParams } from '../../api/querying/listQueryParams';
import type { ReviewListModel } from '../../types/review.types';
import { formatDateTime } from '../../utils/formatDateTime';
import { useWindowListVirtualizer } from '../../hooks/useWindowListVirtualizer';
import { useFetchMoreOnScroll } from '../../hooks/useFetchMoreOnScroll';
import { useFitColumnSizing, type FitColumn } from '../../hooks/useFitColumnSizing';
import { ResizableHeaderCell } from '../../components/grid/ResizableHeaderCell';
import { getGridRowHoverColor, gridRowBackground, gridSkeletonBackground } from '../../components/grid/gridBackgrounds';
import { HeaderLabel, SortableHeaderLabel } from '../../components/grid/SortableHeaderCell';

const PAGE_SIZE = 25;
const ROW_HEIGHT_DESKTOP = 56;
const ROW_HEIGHT_MOBILE = 88;
const ROW_HORIZONTAL_PADDING = 32; // px: 2 on each side of header and body rows

// Initial column widths, scaled proportionally to fill the grid's width.
const COLUMN_SIZES: FitColumn[] = [
    { id: 'title', size: 700 },
    { id: 'owner', size: 160 },
    { id: 'lastModified', size: 200 },
];

const DEFAULT_SORTING: SortingState = [{ id: 'lastModified', desc: true }];

const sortingToOrderBy = (sort: SortingState[number] | undefined) =>
    sort ? `${sort.id} ${sort.desc ? 'desc' : 'asc'}` : undefined;

export const DEFAULT_REVIEWS_QUERY_PARAMS: Omit<ListQueryParams, 'skip'> = {
    top: PAGE_SIZE,
    orderBy: sortingToOrderBy(DEFAULT_SORTING[0]),
};

// Static table inputs — kept outside the component so feature registration
// and the column helper's type parameters stay stable across renders.
const features = tableFeatures({
    rowSortingFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns: { alphanumeric: sortFn_alphanumeric },
});

const columnHelper = createColumnHelper<typeof features, ReviewListModel>();

const columns = columnHelper.columns([
    columnHelper.accessor('title', {
        header: 'Title',
        cell: ({ getValue }) => <Typography noWrap sx={{ fontWeight: 600 }}>{getValue()}</Typography>,
    }),
    columnHelper.accessor('owner', {
        header: 'Owner',
        cell: ({ getValue }) => <Typography noWrap sx={{ color: 'text.secondary' }}>{getValue()}</Typography>,
    }),
    columnHelper.accessor(review => review.lastModified ?? review.created, {
        id: 'lastModified',
        header: 'Last updated',
        cell: ({ getValue }) => <Typography noWrap sx={{ color: 'text.secondary' }}>{formatDateTime(getValue())}</Typography>,
    }),
]);

export const ReviewsList = ({ storageKey }: { storageKey: string }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const rowHeight = isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP;

    const [sorting, setSorting] = React.useState<SortingState>(DEFAULT_SORTING);
    const orderBy = sortingToOrderBy(sorting[0]);

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery(
        getReviewsInfiniteQuery({ top: PAGE_SIZE, orderBy })
    );

    const flatRecords = React.useMemo(() => data?.pages.flatMap(page => page.value) ?? [], [data]);

    const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
        setSorting(updater);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    const table = useTable(
        {
            features,
            columns,
            data: flatRecords,
            state: { sorting },
            onSortingChange: handleSortingChange,
            manualSorting: true,
            enableSortingRemoval: false,
            getRowId: (record) => record.id,
        },
        (state) => state
    );

    const rows = table.getRowModel().rows;

    const { listRef, virtualizer, listHeight } = useWindowListVirtualizer({ count: rows.length, rowHeight });

    const virtualItems = virtualizer.getVirtualItems();

    const { containerRef, getColumnWidth, getResizeHandler, resetSizes } = useFitColumnSizing({
        columns: COLUMN_SIZES,
        horizontalPadding: ROW_HORIZONTAL_PADDING,
        storageKey,
    });

    const { checkWindow } = useFetchMoreOnScroll({ hasNextPage, isFetchingNextPage, fetchNextPage });

    React.useEffect(() => {
        // Also check on mount / after data changes, in case the loaded
        // content doesn't yet fill the viewport (nothing to scroll, so no
        // scroll event would ever fire to trigger the next fetch).
        checkWindow();
        window.addEventListener('scroll', checkWindow);
        return () => window.removeEventListener('scroll', checkWindow);
    }, [checkWindow]);

    const handleRowClick = (review: ReviewListModel) => {
        navigate({
            to: '/reviews/$reviewId/overview',
            params: { reviewId: review.id }
        });
    };

    if (isPending) {
        return (
            <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (rows.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                No reviews yet.
            </Typography>
        );
    }

    return (
        <Box ref={containerRef}>
            {!isMobile && table.getHeaderGroups().map(headerGroup => (
                <Box key={headerGroup.id} sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                    {headerGroup.headers.map(header => {
                        const label = header.column.columnDef.header as string;
                        const content = header.column.getCanSort() ? (
                            <SortableHeaderLabel
                                label={label}
                                isSorted={header.column.getIsSorted()}
                                onClick={header.column.getToggleSortingHandler()}
                            />
                        ) : (
                            <HeaderLabel label={label} />
                        );

                        return (
                            <ResizableHeaderCell
                                key={header.id}
                                width={getColumnWidth(header.column.id)}
                                onResizeStart={getResizeHandler(header.column.id)}
                                onResetSize={resetSizes}
                            >
                                {content}
                            </ResizableHeaderCell>
                        );
                    })}
                </Box>
            ))}

            <Box
                ref={listRef}
                sx={(theme) => ({
                    position: 'relative',
                    width: '100%',
                    height: listHeight,
                    ...gridSkeletonBackground(theme, rowHeight, isMobile),
                })}
            >
                {virtualItems.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    if (!row) return null;
                    const review = row.original;

                    return (
                        <Box
                            key={virtualRow.key}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: virtualRow.size,
                                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                                ...gridRowBackground(theme, virtualRow.index, { hover: false }),
                            }}
                        >
                            <ListItemButton divider onClick={() => handleRowClick(review)} sx={{ height: '100%', '&:hover': { bgcolor: getGridRowHoverColor(theme) } }}>
                                {isMobile ? (
                                    <ListItemText
                                        primary={review.title}
                                        secondary={`${review.owner} · ${formatDateTime(review.lastModified ?? review.created)}`}
                                        slotProps={{
                                            primary: { noWrap: true, fontWeight: 600 },
                                            secondary: { noWrap: true }
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        {row.getAllCells().map(cell => (
                                            <Box
                                                key={cell.id}
                                                style={{ width: getColumnWidth(cell.column.id) }}
                                                sx={{ flexShrink: 0, pr: 3, display: 'flex', alignItems: 'center' }}
                                            >
                                                <table.FlexRender cell={cell} />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </ListItemButton>
                        </Box>
                    );
                })}
                {isFetchingNextPage && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: listHeight,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            py: 2
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
